import { Component, ElementRef, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { fromEvent, ReplaySubject, Subject, Subscription } from 'rxjs';
import { filter, skipWhile, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as ShopCartActions from '../../actions/shop-cart.actions';
import { State } from '../../reducers';
import { I18nService } from '../../services';
import { MarketingFacade } from '../../facades/marketing.facade';
import { StoreConfigurationFacade } from '../../facades/store-configuration.facade';
import { ICart, ICartBaseInformation, ICartRule } from '../../models/cart.models';
import { AppUtils } from '../../utils/app.utils';
import { CartUtils } from '../../utils/cart.utils';
import { CpqUtils } from '../../utils/cpq.utils';
import { MathUtils } from '../../utils/math.utils';
import { IconType } from '../../models/settings.model';
import { EFeatureToggles, EStoreFeatures, EStoreTypes, EUserRoles } from '../../configurations/common';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { AnalyticsService } from '../../analytics/analytics.service';
import { CustomerFacade } from '../../facades/customer.facade';

@Component({
  selector: 'app-mini-cart',
  templateUrl: './mini-cart.component.html',
  styleUrls: ['./mini-cart.component.scss'],
  standalone: false,
})
export class MiniCartComponent implements OnInit, OnDestroy {
  @Input() haveAccess!: boolean;
  @Input() rfqActive!: boolean;
  @Input() isRfqOnly!: boolean;

  isOpen!: boolean;
  iconType: typeof IconType = IconType;

  isUsStore: boolean = false;
  isCaStore: boolean = false;
  isCpqEnabled: boolean = false;
  isExcludeTaxActive: boolean = false;
  isConfigurationPage: boolean = false;
  isCartOperationInProgress: boolean = false;
  isLastAddedItemLoaded!: boolean;
  showNewItemAddedNotification!: boolean;
  showCartSwitchDecisionModal: boolean = false;
  isDeliveryDetailsOrCheckoutPage: boolean = false;
  isOrderReviewPage: boolean = false;
  inProcessCartId!: string;
  addNicknameModalActive: boolean = false;
  deleteQuoteModalActive: boolean = false;
  deleteCartModalActive: boolean = false;
  showPriceDisclaimer!: boolean;
  isCartEmpty: boolean = true;
  cartRules!: Array<ICartRule>;
  currentCart!: ICart;
  usersCarts: ICartBaseInformation[] = [];
  itemsInCart!: any[];
  itemName!: string;
  currency!: string;
  totalPrice!: number;
  priceToPay!: number;
  minimumOrderValue: number = 0;
  hasNickname: boolean = false;
  currentCartName: string = '';
  currentCartName$: ReplaySubject<string> = new ReplaySubject<string>();
  companyRoles!: EUserRoles[];
  redirectToHomepageOnCartOperationFinish: boolean = false;
  documentClick!: Subscription;
  isSapIntegration: boolean = false;
  isSapIntegrationRequired: boolean = false;

  private readonly unsubscribe$: Subject<void> = new Subject<void>();

  private readonly miniCartRef: ElementRef = inject(ElementRef);
  private readonly router: Router = inject(Router);
  private readonly store: Store<State> = inject<Store<State>>(Store);
  private readonly marketingFacade: MarketingFacade = inject(MarketingFacade);
  private readonly i18nService: I18nService = inject(I18nService);
  private readonly storeConfigurationFacade: StoreConfigurationFacade = inject(StoreConfigurationFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);

  ngOnInit(): void {
    this.documentClick = fromEvent(document, 'mousedown')
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((event: Event) => {
        if (!this.miniCartRef.nativeElement.contains(event.target)) {
          this.closeMiniCart();
        }
      });

    this.checkUrlIncludes();

    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.isCaStore = AppUtils.isStoreActive(EStoreTypes.CA);
    this.isExcludeTaxActive = this.configurationFacade.isFeatureAvailable(EStoreFeatures.EXCLUDE_TAX);
    this.showPriceDisclaimer = AppUtils.getCurrentStore().showPriceDisclaimer;

    this.configurationFacade.getSapStatus().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(({sapIntegration, sapIntegrationRequired}) => {
      this.isSapIntegration = sapIntegration;
      this.isSapIntegrationRequired = sapIntegrationRequired;
    });

    this.getCompanyRoles();
    this.selectMinimumOrderValue();
    this.selectUsFeatures();
    this.selectMiniCartState();
    this.selectCart();
    this.selectCartItemsWithDetails();
    this.selectCartRules();
    this.selectLastAddedItemName();
    this.selectUserCartsBaseInformation();
    this.selectCartOperationInProgress();
  }

  ngOnDestroy(): void {
    this.documentClick.unsubscribe();
    this.documentClick = undefined!;
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private checkUrlIncludes(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.unsubscribe$),
    ).subscribe((_: NavigationEnd) => {
      this.isConfigurationPage = this.router.url.includes('/service-configuration');
      this.isDeliveryDetailsOrCheckoutPage = this.router.url.includes('delivery-details') || this.router.url.includes('order-approval');
      this.isOrderReviewPage = this.router.url.includes('order-review');
      if (this.router.url.includes('lastCartId')) {
        this.inProcessCartId = this.router.parseUrl(this.router.url).toString().split('=')[1];
        this.usersCarts.forEach(cart => {
          if (cart.id === this.inProcessCartId) {
            cart.approverId = 'notNull';
          }
        });
      }
    });
  }

  private getCompanyRoles(): void {
    this.customerFacade.getCustomerCompanyRoles().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(companyRoles => {
      this.companyRoles = companyRoles;
    });
  }

  isApprover(): boolean {
    return this.companyRoles.includes(EUserRoles.Approver);
  }

  selectUsFeatures(): void {
    this.configurationFacade.isFeatureEnabled(EFeatureToggles.CPQ).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(value => {
      this.isCpqEnabled = value;
    });
  }

  switchToNewCart(): void {
    this.marketingFacade.switchToNewCart();
  }

  stayOnCurrentCart(): void {
    this.marketingFacade.stayOnCurrentCart();
  }

  closeMiniCart(): void {
    if (!this.isOpen) {
      return; // Prevent unnecessary state changes if already closed
    }

    this.isOpen = false;
    this.marketingFacade.toggleMiniCart(this.isOpen); // Update Redux state only if necessary
  }

  isContractInCart(): boolean {
    return !!this.currentCart?.attributes?.hasContractInCart;
  }

  isMinimumOrderValue(): boolean {
    return CartUtils.getIsMinimumOrderValue(this.minimumOrderValue, this.isCartEmpty, this.currentCart, this.itemsInCart);
  }

  checkWorkflowInformation(): boolean {
    if (this.isUsStore || this.isCaStore) {
      return this.isContractInCart();
    } else {
      return !this.rfqActive || (this.isRfqOnly && CartUtils.isSetPricesForAllItems(this.itemsInCart));
    }
  }

  getLabelForCartButton(): string {
    return this.isContractInCart() ? 'mini-cart.go-to-quote' : 'mini-cart.go-to-cart';
  }

  deleteQuote(): void {
    this.marketingFacade.deleteCpqCart(this.currentCart.id);

    if (this.isConfigurationPage) {
      this.router.navigate([this.i18nService.getCurrentLocale()]);
    }
  }

  /**
   * This method is for deleting cart.
   * At first, this method is checking if this cart had a approverId via getNotInProcessCartsOrNull().
   */
  deleteCart(): void {
    const deletingCurrentCartId: string = this.currentCart.id;
    this.marketingFacade.deleteCart(this.currentCart.id);
    const newDefaultCartId: string | null = this.getNotInProcessCartsOrNull();
    if (!newDefaultCartId) {
      this.marketingFacade.createEmptyCart();
    } else if (!(deletingCurrentCartId === this.currentCart.id && this.usersCarts.length === 1)) {
      this.usersCarts = this.usersCarts.filter(cart => cart.id !== this.currentCart.id);
    }

    if (this.isDeliveryDetailsOrCheckoutPage || this.isOrderReviewPage) {
      this.router.navigate(['/shop-cart']).then(() => this.marketingFacade.resetCachedCheckoutData());
      this.closeMiniCart();
    }
  }

  /**
   * Returns id of the first other cart without approver which is not currently in approval process.
   * Carts with approverId are relevant for stores with quote approval (feature_toggle.checkout_always_with_quote_approval).
   * @returns {string | null}
   */
  getNotInProcessCartsOrNull(): string | null {
    const notInProcessCart: ICartBaseInformation | undefined = this.usersCarts.find(cart =>
      !cart.approverId && cart.id !== this.currentCart.id && cart.id !== this.inProcessCartId,
    );

    return notInProcessCart?.id ?? null;
  }

  updateCartItemQuantity(data: any): void {
    const {oldQuantity, newQuantity, itemId, itemName} = data;
    const updatedQty: number = parseInt(newQuantity, 10);

    if (!MathUtils.checkIfNumeric(updatedQty)) {
      return;
    }

    const quantityDiff: number = +newQuantity - +oldQuantity;
    if (quantityDiff > 0) {
      this.trackIncreaseQuantity(itemId, itemName, quantityDiff);
    } else if (quantityDiff < 0) {
      this.trackDecreaseQuantity(itemId, itemName, -quantityDiff);
    }

    this.store.dispatch(ShopCartActions.setUpdateItemInCartData({quantity: updatedQty}));

    this.store.dispatch(ShopCartActions.updateItemInCartLoadCartItems({
      cartId: this.currentCart.id,
      itemId: itemId,
      updateItemInCartData: {
        'data': {
          'type': 'items',
          'attributes': {
            'quantity': newQuantity,
          },
        },
      },
    }));
  }

  private selectMinimumOrderValue(): void {
    this.storeConfigurationFacade.selectMinimumOrderValue().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.minimumOrderValue = state;
    });
  }

  private selectCart(): void {
    this.marketingFacade.selectCart().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      if (!state) {
        this.totalPrice = 0;
        this.currency = AppUtils.getCurrentStore().storeDefaultCurrency;
        return;
      }

      this.currentCart = state;
      this.totalPrice = this.isCpqEnabled
        ? this.currentCart.attributes.totals.cpqNetPriceTotal || 0
        : this.currentCart.attributes.totals.subtotal || 0;
      this.priceToPay = this.currentCart.attributes.totals.priceToPay || 0;
      this.currency = this.currentCart.attributes.currency;
      this.currentCartName = CartUtils.getCartName(this.currentCart);
      this.hasNickname = !MathUtils.checkIfNumeric(this.currentCart.attributes.name);
      this.currentCartName$.next(this.hasNickname ? this.currentCartName : '');
    });
  }

  selectUserCartsBaseInformation(): void {
    this.marketingFacade.selectCartsBaseInfo()
      .pipe(
        skipWhile(baseCartsInfo => baseCartsInfo.length === 0),
        takeUntil(this.unsubscribe$),
      ).subscribe(baseCartsInfo => {
      this.usersCarts = baseCartsInfo;
    });
  }

  private selectCartItemsWithDetails(): void {
    this.marketingFacade.selectCartItemsWithDetails().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(items => {
      this.itemsInCart = this.isCpqEnabled && this.isContractInCart() && items
        ? CpqUtils.mergeCpqItems(items)
        : items;
    });
  }

  private selectCartRules(): void {
    this.marketingFacade.selectCartRules().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(cartRules => {
      this.cartRules = cartRules;
    });
  }

  private selectMiniCartState(): void {
    this.marketingFacade.selectMiniCartState().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(state => {
      this.isOpen = this.shouldCloseMiniCart(this.router.url) ? false : state.isMiniCartOpen;
      this.isCartEmpty = state.isCartEmpty;
      this.showNewItemAddedNotification = state.showNewItemAddedNotification;
      this.showCartSwitchDecisionModal = state.showCartSwitchDecisionModal;

      if (this.showNewItemAddedNotification) {
        this.configurationFacade.clearNotifications();
      }
    });
  }

  /**
   * Determines if the mini-cart should be closed based on the current URL.
   *
   * @param {string} url - The current URL to check.
   * @returns {boolean} - Returns `true` if the mini-cart should be closed, otherwise `false`.
   */
  private shouldCloseMiniCart(url: string): boolean {
    if (!url) {
      return false;
    }
    const pathsToClose: string[] = ['shop-cart', 'sap-delivery-details', 'order-review', 'delivery-details', 'order-review', 'request-quote'];
    return pathsToClose.some(path => url.includes(path));
  }

  private selectLastAddedItemName(): void {
    this.marketingFacade.selectLastAddedItemName().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(item => {
      this.itemName = item || '';
      this.isLastAddedItemLoaded = true;
    });
  }

  private selectCartOperationInProgress(): void {
    this.marketingFacade.selectCartOperationInProgress().pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(inProgress => {
      this.isCartOperationInProgress = inProgress;

      if (this.redirectToHomepageOnCartOperationFinish && !this.isCartOperationInProgress) {
        this.redirectToHomepageOnCartOperationFinish = false;
        this.router.navigate([this.i18nService.getCurrentLocale()]).then(() => this.marketingFacade.resetCachedCheckoutData());
        this.closeMiniCart();
      }
    });
  }

  isMinicartAccessible(): boolean {
    return this.haveAccess && ((this.isSapIntegrationRequired && this.isSapIntegration) || (!this.isSapIntegrationRequired));
  }

  private trackIncreaseQuantity(itemId: string, itemName: string, quantityDiff: number): void {
    this.analyticsService.setProducts({
      sku: itemId,
      name: itemName,
      quantity: quantityDiff,
    });

    this.analyticsService.trackCart('cart.add');
  }

  private trackDecreaseQuantity(itemId: string, itemName: string, quantityDiff: number): void {
    this.analyticsService.setProducts({
      sku: itemId,
      name: itemName,
      quantity: quantityDiff,
    });

    this.analyticsService.trackCart('cart.remove');
  }
}
